import { useState } from "react";

interface UsePaginatedSearchOptions {
    /** Current page number - a new search always jumps back to page 1. */
    page: number;
    setPage: (page: number) => void;
    /** Reloads data using the latest `committedSearchTerm`. */
    load: () => void;
    initialValue?: string;
}

/**
 * Shared search-box state for the paginated Rheem tables (Indicators, Event
 * Candidates, Notes). `searchTerm` is the live text box value; `committedSearchTerm`
 * is only updated when the user submits (Search button / Enter / Clear)
 */
export function usePaginatedSearch({ page, setPage, load, initialValue = "" }: UsePaginatedSearchOptions) {
    const [searchTerm, setSearchTerm] = useState(initialValue);
    const [committedSearchTerm, setCommittedSearchTerm] = useState(initialValue);

    const applySearch = (nextTerm: string) => {
        const trimmedTerm = nextTerm.trim();
        const pageChanging = page !== 1;
        const termChanging = committedSearchTerm !== trimmedTerm;

        if (pageChanging) setPage(1);
        if (termChanging) setCommittedSearchTerm(trimmedTerm);

        // Neither state change would trigger a reload on its own (values are
        // already current) - reload directly so "Search"/"Clear" always does something.
        if (!pageChanging && !termChanging) {
            load();
        }
    };

    const doSearch = () => {
        const trimmedTerm = searchTerm.trim();
        if (trimmedTerm !== searchTerm) setSearchTerm(trimmedTerm);
        applySearch(trimmedTerm);
    };

    const clearSearch = () => {
        setSearchTerm("");
        applySearch("");
    };

    return { searchTerm, setSearchTerm, committedSearchTerm, doSearch, clearSearch };
}
